import React, { useState, useEffect } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client/react';
import { CombinedGraphQLErrors } from '@apollo/client';
import { UPDATE_LOAD_DETAIL, ASSIGN_DRIVER_TO_LOAD, UPDATE_LOAD_ACCESSORIAL } from '../../../graphql/client/mutations';
import {
    GET_LOAD_DETAIL,
    GET_LOAD_ACCESSORIAL,
    GET_LOAD_LOGGERS,
    GET_TMS_DUPLICATES,
} from '../../../graphql/client/queries';
import { LoadAccessorialEnum } from '../../../types/LoadAccessorialEnum';
import { AssignDriverToLoad, GetTmsDuplicates } from '../../../types/schemaTypes';

interface Suggestion {
    id: string | number;
    name: string;
}

const useEditLoadDetail = (
    loadDetailData: any,
    loadId: number,
    onClickHide: () => void,
    miles: string,
    chainUpCheckStatus: string,
    deadHeadCheckStatus: string,
    onUpdateError: () => void,
    refetch: () => void,
    validateSBXBox: any,
): any => {
    const [inputs, setInputs] = useState({
        loadId: loadId,
        boxNumber: loadDetailData.boxNumber ? loadDetailData.boxNumber : '',
        driverName: loadDetailData.driverName ? loadDetailData.driverName : '',
        truckNumber: loadDetailData.truckNumber ? loadDetailData.truckNumber : '',
        chassisNumber: loadDetailData.chassisNumber ? loadDetailData.chassisNumber : '',
        status: loadDetailData.status ? loadDetailData.status : '',
        isSplit: loadDetailData.isSplit,
        secondaryBoxNumber: loadDetailData.boxNumber ? loadDetailData.boxNumber : '',
        secondaryDriverId: loadDetailData.secondaryDriverId ? loadDetailData.secondaryDriverId : 0,
        secondaryDriverName: loadDetailData.secondaryDriverName ? loadDetailData.secondaryDriverName : '',
        secondaryTruckNumber: loadDetailData.secondaryTruckNumber ? loadDetailData.secondaryTruckNumber : '',
        secondaryChassisNumber: loadDetailData.secondaryChassisNumber ? loadDetailData.secondaryChassisNumber : '',
        secondaryDriverLoadStatus: loadDetailData.secondaryDriverLoadStatus
            ? loadDetailData.secondaryDriverLoadStatus
            : '',
        tmsId: loadDetailData.tmsId ? loadDetailData.tmsId : '',
        driverLoadStatus: loadDetailData.driverLoadStatus ? loadDetailData.driverLoadStatus : '',
        driverId: loadDetailData.driverId ? loadDetailData.driverId : 0,
        miles: miles || '',
        chainUpCheckStatus: chainUpCheckStatus || '',
        deadHeadCheckStatus: deadHeadCheckStatus || '',
        transportationPo: loadDetailData.transportationPo ? loadDetailData.transportationPo : '',
        boxId: loadDetailData.boxId ? loadDetailData.boxId : 0,
        chassisId: loadDetailData.chassisId ? loadDetailData.chassisId : 0,
        secondaryChassisId: loadDetailData.secondaryChassisId ? loadDetailData.secondaryChassisId : 0,
    });

    const [milesError, setMilesError] = useState('');
    const [boxError, setBoxError] = useState('');
    const [secondaryBoxError, setSecondaryBoxError] = useState('');
    const [chassisError, setChassisError] = useState('');
    const [secondaryChassisError, setSecondaryChassisError] = useState('');
    const [tmsIdError, setTmsIdError] = useState('');
    const [truckNumberError, setTruckNumberError] = useState('');
    const [secondaryTruckNumberError, setSecondaryTruckNumberError] = useState('');
    const [concurrentError, setConcurrentError] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [getDuplicates, { data: countDuplicates, loading: countDuplicatesLoading }] = useLazyQuery<GetTmsDuplicates>(
        GET_TMS_DUPLICATES,
        { fetchPolicy: 'network-only' },
    );

    const handleCancel = (): void => {
        if (concurrentError !== '') {
        } else {
            setMilesError('');
            setBoxError('');
            setSecondaryBoxError('');
            setChassisError('');
            setSecondaryChassisError('');
            setTruckNumberError('');
            setSecondaryTruckNumberError('');
            setTmsIdError('');
            setErrorMessage('');
            setInputs({
                ...inputs,
                deadHeadCheckStatus: deadHeadCheckStatus || '',
                chainUpCheckStatus: chainUpCheckStatus || '',
                miles: miles || '',
                tmsId: loadDetailData.tmsId || '',
                boxNumber: loadDetailData.boxNumber || '',
                boxId: loadDetailData.boxId || 0,
                chassisId: loadDetailData.chassisId || 0,
                secondaryChassisId: loadDetailData.secondaryChassisId || 0,
                driverName: loadDetailData.driverName || '',
                truckNumber: loadDetailData.truckNumber || '',
                chassisNumber: loadDetailData.chassisNumber || '',
                secondaryBoxNumber: loadDetailData.boxNumber || '',
                secondaryDriverId: loadDetailData.secondaryDriverId || 0,
                secondaryDriverName: loadDetailData.secondaryDriverName || '',
                secondaryTruckNumber: loadDetailData.secondaryTruckNumber || '',
                secondaryChassisNumber: loadDetailData.secondaryChassisNumber || '',
                secondaryDriverLoadStatus: loadDetailData.secondaryDriverLoadStatus || '',
                driverLoadStatus: loadDetailData.driverLoadStatus || '',
                driverId: loadDetailData.driverId || 0,
            });
            onClickHide();
        }
    };
    const handleComplete = (): void => {
        if (concurrentError !== '') {
        } else {
            setMilesError('');
            setBoxError('');
            setSecondaryBoxError('');
            setChassisError('');
            setSecondaryChassisError('');
            setTruckNumberError('');
            setSecondaryTruckNumberError('');
            setTmsIdError('');
            setErrorMessage('');
            setInputs({
                ...inputs,
                deadHeadCheckStatus: deadHeadCheckStatus || '',
                chainUpCheckStatus: chainUpCheckStatus || '',
                miles: miles || '',
            });
            refetch();
            onClickHide();
        }
    };
    const [updateLoadDetail] = useMutation(UPDATE_LOAD_DETAIL, {
        onCompleted: handleComplete,
        onError: (error) => {
            if (CombinedGraphQLErrors.is(error)) {
                onUpdateError();
                setErrorMessage(error.errors[0]?.message || error.message);
            } else if (error) {
                onUpdateError();
                setErrorMessage(error.message);
            }
        },
        refetchQueries: () => {
            return [
                {
                    query: GET_LOAD_DETAIL,
                    variables: { loadId: loadDetailData.loadId },
                },
                {
                    query: GET_LOAD_LOGGERS,
                    variables: {
                        input: {
                            loadId: loadId,
                            search: '',
                        },
                    },
                },
            ];
        },
    });

    const loadDetailMutationData = (calledFromAssignDriver?: boolean): void => {
        const updateLoadDetailEditFields: any = {
            driverLoadStatus:
                loadDetailData.driverLoadStatus !== inputs.driverLoadStatus ? inputs.driverLoadStatus : null,
            boxNumber: loadDetailData.boxNumber !== inputs.boxNumber ? inputs.boxNumber : null,
            boxId: loadDetailData.boxId !== inputs.boxId ? inputs.boxId : 0,
            chassisId: loadDetailData.chassisId !== inputs.chassisId ? inputs.chassisId : 0,
            chassisNumber: loadDetailData.chassisNumber !== inputs.chassisNumber ? inputs.chassisNumber : null,
            truckNumber: loadDetailData.truckNumber !== inputs.truckNumber ? inputs.truckNumber : null,
            secondaryDriverLoadStatus:
                loadDetailData.secondaryDriverLoadStatus !== inputs.secondaryDriverLoadStatus
                    ? inputs.secondaryDriverLoadStatus
                    : null,
            secondaryChassisId:
                loadDetailData.secondaryChassisId !== inputs.secondaryChassisId ? inputs.secondaryChassisId : 0,
            secondaryChassisNumber:
                loadDetailData.secondaryChassisNumber !== inputs.secondaryChassisNumber
                    ? inputs.secondaryChassisNumber
                    : null,
            secondaryTruckNumber:
                loadDetailData.secondaryTruckNumber !== inputs.secondaryTruckNumber
                    ? inputs.secondaryTruckNumber
                    : null,
            leanId: loadDetailData.tmsId !== inputs.tmsId ? inputs.tmsId : null,
            transportationPo:
                loadDetailData.transportationPo !== inputs.transportationPo ? inputs.transportationPo : null,
            isBellyDump: loadDetailData.isBellyDump ? loadDetailData.isBellyDump: false
        };
        updateLoadDetail({
            variables: {
                input: {
                    loadId: inputs.loadId,
                    newValues: updateLoadDetailEditFields,
                    currentValues: {
                        driverLoadStatus: calledFromAssignDriver ? 'Pending' : loadDetailData.driverLoadStatus || '',
                        boxId: loadDetailData.boxId || 0,
                        boxNumber: loadDetailData.boxNumber || '',
                        chassisId: loadDetailData.chassisId || 0,
                        chassisNumber: loadDetailData.chassisNumber || '',
                        truckNumber: loadDetailData.truckNumber || '',
                        secondaryDriverLoadStatus: calledFromAssignDriver
                            ? 'Pending'
                            : loadDetailData.secondaryDriverLoadStatus || '',
                        secondaryChassisNumber: loadDetailData.secondaryChassisNumber || '',
                        secondaryChassisId: loadDetailData.secondaryChassisId || 0,
                        secondaryTruckNumber: loadDetailData.secondaryTruckNumber || '',
                        leanId: loadDetailData.tmsId || '',
                        transportationPo: loadDetailData.transportationPo || '',
                        isBellyDump: loadDetailData.isBellyDump || false,
                    },
                },
            },
        });
    };

    const onAssignDriver = (data: AssignDriverToLoad): void => {
        if (data.assignDriverToLoad === null) return;
        const assignDriverErrors = data.assignDriverToLoad.errors;
        if (assignDriverErrors && assignDriverErrors.length > 0) {
            setErrorMessage(assignDriverErrors[0] || '');
        } else {
            refetch();
            loadDetailMutationData(true);
        }
    };

    const [updateDriver] = useMutation<AssignDriverToLoad>(ASSIGN_DRIVER_TO_LOAD, {
        onCompleted: onAssignDriver,
        onError: (e) => {
            if (CombinedGraphQLErrors.is(e)) {
                console.log('GraphQL Error', e.errors);
            } else {
                console.log('Error', e.message);
            }
        },
        refetchQueries: () => {
            return [
                {
                    query: GET_LOAD_DETAIL,
                    variables: { loadId: loadDetailData.loadId },
                },
                {
                    query: GET_LOAD_LOGGERS,
                    variables: {
                        input: {
                            loadId: loadId,
                            search: '',
                        },
                    },
                },
            ];
        },
    });

    const [updateLoadAccessorialDeadHead] = useMutation(UPDATE_LOAD_ACCESSORIAL, {
        onError: () => {
            onUpdateError();
            setConcurrentError('error');
        },
        refetchQueries: () => {
            return [
                {
                    query: GET_LOAD_ACCESSORIAL,
                    variables: {
                        input: {
                            loadId: loadId,
                            accessorialName: [LoadAccessorialEnum.DEAD_HEAD, LoadAccessorialEnum.CHAINUP_TOWUP],
                        },
                    },
                },
                {
                    query: GET_LOAD_LOGGERS,
                    variables: {
                        input: {
                            loadId: loadId,
                            search: '',
                        },
                    },
                },
            ];
        },
    });

    const validateInputs = (name: string, value: string): void => {
        const alphanumeric = /^[a-z0-9]+$/i;

        switch (name) {
            case 'miles':
                if ((value === '' || +value === 0) && inputs.deadHeadCheckStatus === 'Y') {
                    setMilesError('Miles should be greater than 0.');
                } else {
                    setMilesError('');
                }
                break;
            case 'truckNumber':
                if (inputs.driverLoadStatus === 'Accepted' && value.length == 0) {
                    setTruckNumberError('Truck Number should be 50 or less alphanumeric characters.');
                    break;
                }
                if (value.length === 0) {
                    setTruckNumberError('');
                } else {
                    if (value.length > 50 || !alphanumeric.test(value)) {
                        setTruckNumberError('Truck Number should be 50 or less alphanumeric characters.');
                    } else {
                        setTruckNumberError('');
                    }
                }
                break;
            case 'secondaryTruckNumber':
                if (inputs.secondaryDriverLoadStatus === 'Accepted' && value.length == 0) {
                    setSecondaryTruckNumberError('Truck Number must be entered when Accepting a load.');
                    break;
                }
                if (value.length === 0) {
                    setSecondaryTruckNumberError('');
                } else {
                    if (value.length > 50 || !alphanumeric.test(value)) {
                        setSecondaryTruckNumberError('Truck Number should be 50 or less alphanumeric characters.');
                    } else {
                        setSecondaryTruckNumberError('');
                    }
                }
                break;
            case 'tmsId':
                if (
                    value === '' &&
                    (loadDetailData.customerId === 46 ||
                        loadDetailData.customerId === 6 ||
                        loadDetailData.customerId === 85)
                ) {
                    setTmsIdError('TmsId should not be empty for Halliburton customer');
                } else {
                    setTmsIdError('');
                }
                break;
        }
    };

    const checkForRequiredInput = (newValue: string): boolean => {
        const { truckNumber, chassisNumber, boxNumber, isSplit, secondaryChassisNumber, secondaryTruckNumber } = inputs;

        setTruckNumberError('');
        setChassisError('');
        setBoxError('');
        setSecondaryBoxError('');

        setSecondaryTruckNumberError('');
        setSecondaryChassisError('');

     //   const alphanumeric = /^[a-z0-9]+$/i;
        const alphanumeric = /^[a-zA-Z0-9._-]+$/i;
     

        let valid = true;

        let value;

        // If it's entered, it need to be correct
        if (boxNumber && boxNumber.toString().length > 0) {
            value = boxNumber;
            if (value.length > 11 || !alphanumeric.test(value)) {
                setBoxError('Box Number should be 11 or less alphanumeric characters.');
                valid = false;
            }
        }

        if (chassisNumber && chassisNumber.toString().length > 0) {
            value = chassisNumber;
            if (value.length > 10 || !alphanumeric.test(value)) {
                setChassisError('Chassis Number should be 10 or less alphanumeric characters.');
                valid = false;
            }
        }

        if (truckNumber && truckNumber.toString().length > 0) {
            value = truckNumber;
            if (value.length > 50 || !alphanumeric.test(value)) {
                setTruckNumberError('Truck Number should be 50 or less alphanumeric characters.');
                valid = false;
            }
        }

        if (isSplit) {
            if (boxNumber && boxNumber.toString().length > 0) {
                value = boxNumber;
                if (value.length > 11 || !alphanumeric.test(value)) {
                    setSecondaryBoxError('Box Number should be 11 or less alphanumeric characters.');
                    valid = false;
                }
            }
        }

        if (secondaryChassisNumber && secondaryChassisNumber.toString().length > 0) {
            value = secondaryChassisNumber;

            if (value.length > 10 || !alphanumeric.test(value)) {
                setChassisError('Chassis Number should be 10 or less alphanumeric characters.');
                valid = false;
            }
        }

        if (secondaryTruckNumber && secondaryTruckNumber.toString().length > 0) {
            value = secondaryTruckNumber;
            if (value.length > 50 || !alphanumeric.test(value)) {
                setSecondaryTruckNumberError('Truck Number should be 50 or less alphanumeric characters.');
                valid = false;
            }
        }

        if (newValue !== 'Accepted') return valid;

        if (isSplit) {
            if (!inputs.boxId || inputs.boxId == 0) {
                setSecondaryBoxError('Box Number must be entered when Accepting a load.');
                valid = false;
            }
            if (!secondaryTruckNumber || secondaryTruckNumber.toString().length == 0) {
                setSecondaryTruckNumberError('Truck Number must be entered when Accepting a load.');
                valid = false;
            }
            if ((!inputs.secondaryChassisId || inputs.secondaryChassisId == 0) && inputs.secondaryChassisNumber === "") {
                setSecondaryChassisError('Chassis Number must be entered when Accepting a load.');
                valid = false;
            }
        } else {
            if (!loadDetailData.isBellyDump && (!inputs.boxId || inputs.boxId == 0)) {
                setBoxError('Box Number must be entered when Accepting a load.');
                valid = false;
            }
            if (!loadDetailData.isBellyDump && (!truckNumber || truckNumber.toString().length == 0)) {
                setTruckNumberError('Truck Number must be entered when Accepting a load.');
                valid = false;
            }

            if (!loadDetailData.isBellyDump && (!inputs.chassisId || inputs.chassisId == 0)) {
                setChassisError('Chassis Number must be entered when Accepting a load.');
                valid = false
            }
        }

        return valid;
    };

    const handleDropdownChange = (fieldName: string, newValue: string): void => {
        if (fieldName == 'driverLoadStatus' || fieldName === 'secondaryDriverLoadStatus') {
            checkForRequiredInput(newValue);
        }
        setInputs(inputs => ({ ...inputs, [fieldName]: newValue }));
        validateInputs(fieldName, newValue);
    };

    const handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ): void => {
        event.persist();
        let target = event.target.name;
        validateInputs(target, event.target.value);
        if (target === 'secondaryBoxNumber') target = 'boxNumber';
        setInputs(inputs => ({ ...inputs, [target]: event.target.value }));
    };
 
    const handleAutoCompleteChange = (inputName: string, value: Suggestion): void => {
        
        switch (inputName) {
            case 'driverName': {
                setInputs({
                    ...inputs,
                    driverName: value.name,
                    driverId: parseInt(value.id.toString()),
                });
                break;
            }
            case 'secondaryDriverName': {
                setInputs({
                    ...inputs,
                    secondaryDriverName: value.name,
                    secondaryDriverId: parseInt(value.id.toString()),
                });
                break;
            }
            case 'boxNumber': {
                if (inputs.isSplit) break;
                if (loadDetailData.isBellyDump){
                    break;
                }
                if (inputs.driverLoadStatus === 'Accepted' && value.id == 0) {
                    setBoxError('Box Number must be entered when Accepting a load.');
                    break;
                } 
                if (loadDetailData.status === 'Delivered' && value.id == 0) {
                    setBoxError('Please select the Box Number from Dropdown.');
                    break;
                } 
                else {
                    setBoxError('');
                }
                setInputs({
                    ...inputs,
                    boxNumber: value.name,
                    boxId: parseInt(value.id.toString()),
                });

                break;
            }
            case 'secondaryBoxNumber': {
                if (inputs.secondaryDriverLoadStatus === 'Accepted' && value.id == 0) {
                    setSecondaryBoxError('Box Number must be entered when Accepting a load.');
                    break;
                } else {
                    setSecondaryBoxError('');
                }
                setInputs({
                    ...inputs,
                    boxNumber: value.name,
                    boxId: parseInt(value.id.toString()),
                });

                break;
            }
            case 'chassisNumber': {
                if(loadDetailData.isBellyDump) break;
                if (inputs.driverLoadStatus === 'Accepted' && value.id == 0) {
                    setChassisError('Chassis Number must be entered when Accepting a load.');
                    break;
                }
                if (loadDetailData.status === 'Delivered' && value.id == 0) {
                    setChassisError('Please select Chassis from Dropdown.');
                    break;
                }  else {
                    setChassisError('');
                }
                setInputs({
                    ...inputs,
                    chassisNumber: value.name,
                    chassisId: parseInt(value.id.toString()),
                });
                break;
            }
            case 'secondaryChassisNumber': {
                if (inputs.secondaryDriverLoadStatus === 'Accepted' && value.id == 0) {
                    setSecondaryChassisError('Chassis Number must be entered when Accepting a load.');
                    
                    break;
                } else {
                    setSecondaryChassisError('');
                }
                setInputs({
                    ...inputs,
                    secondaryChassisNumber: value.name,
                    secondaryChassisId: parseInt(value.id.toString()),
                });
                break;
            }
        }
    };

    const handleCheckBox = (checkFor: string, inputName: string): void => {
        switch (inputName) {
            case 'chainUp': {
                if (checkFor === '' || checkFor === null || checkFor === 'N') {
                    setInputs({
                        ...inputs,
                        chainUpCheckStatus: 'Y',
                    });
                } else {
                    setInputs({
                        ...inputs,
                        chainUpCheckStatus: 'N',
                    });
                }
                break;
            }
            case 'deadHead': {
                if (checkFor === '' || checkFor === null || checkFor === 'N') {
                    setInputs({
                        ...inputs,
                        deadHeadCheckStatus: 'Y',
                    });
                } else {
                    setMilesError('');
                    setInputs({
                        ...inputs,
                        deadHeadCheckStatus: 'N',
                    });
                }
                break;
            }
        }
    };

    const handleOnClickOK = (): void => {
        window.location.reload();
    };

    const deadHeadMutation = (): void => {
        if (inputs.deadHeadCheckStatus !== deadHeadCheckStatus || inputs.miles !== miles) {
            const loadAccessorialsEditFieldsDeadHead: any = {
                deadHeadValue: inputs.miles || '',
                accessorialId: 1,
                checked: inputs.deadHeadCheckStatus,
            };
            updateLoadAccessorialDeadHead({
                variables: {
                    input: {
                        loadId: inputs.loadId,
                        newValues: loadAccessorialsEditFieldsDeadHead,
                        currentValues: {
                            deadHeadValue: miles || '',
                            accessorialId: 1,
                            checked: deadHeadCheckStatus || '',
                        },
                    },
                },
            });
        }
    };

    const chainupMutation = (): void => {
        if (inputs.chainUpCheckStatus !== chainUpCheckStatus) {
            const loadAccessorialsEditFieldsDeadHead: any = {
                deadHeadValue: '',
                accessorialId: 2,
                checked: inputs.chainUpCheckStatus,
            };
            updateLoadAccessorialDeadHead({
                variables: {
                    input: {
                        loadId: inputs.loadId,
                        newValues: loadAccessorialsEditFieldsDeadHead,
                        currentValues: {
                            deadHeadValue: miles || '',
                            accessorialId: 2,
                            checked: chainUpCheckStatus || '',
                        },
                    },
                },
            });
        }
    };

    const callMutationForEditLoadDetail = (): void => {
        // If no errors
        if (
            !milesError &&
            !boxError &&
            !secondaryBoxError &&
            !chassisError &&
            !secondaryChassisError &&
            !truckNumberError &&
            !secondaryTruckNumberError &&
            !tmsIdError
        ) {
            if (
                inputs.tmsId === '' &&
                (loadDetailData.customerId === 46 ||
                    loadDetailData.customerId === 6 ||
                    loadDetailData.customerId === 85)
            ) {
                setTmsIdError('TmsId should not be empty for Halliburton customer');
            } else if ((inputs.miles === '' || +inputs.miles === 0) && inputs.deadHeadCheckStatus === 'Y') {
                setMilesError('Miles should be greater than 0.');
            } else {
                if (loadDetailData.driverId !== inputs.driverId) {
                    deadHeadMutation();
                    chainupMutation();
                    updateDriver({
                        variables: { driverId: inputs.driverId, loadId: inputs.loadId, isPrimaryDriver: true },
                    });
                }
                if (loadDetailData.secondaryDriverId !== inputs.secondaryDriverId) {
                    deadHeadMutation();
                    chainupMutation();
                    updateDriver({
                        variables: {
                            driverId: inputs.secondaryDriverId,
                            loadId: inputs.loadId,
                            isPrimaryDriver: false,
                        },
                    });
                }
                if (
                    loadDetailData.driverId === inputs.driverId &&
                    loadDetailData.secondaryDriverId === inputs.secondaryDriverId
                ) {
                    deadHeadMutation();
                    chainupMutation();
                    loadDetailMutationData();
                }
            }
        }
    };

    useEffect(() => {
        if (!countDuplicatesLoading) {
            const duplicateTmsIds = countDuplicates?.getTmsDuplicates?.duplicateTmsIds ?? [];
            if (duplicateTmsIds.length === 0 && countDuplicates?.getTmsDuplicates != null) {
                setTmsIdError('');
                callMutationForEditLoadDetail();
            } else if (countDuplicates?.getTmsDuplicates != null && duplicateTmsIds.length > 0) {
                setTmsIdError('TMS Id must be unique. Please enter in a new TMS Id.');
            }
        }
    }, [countDuplicates]);

    const checkForTmsIdDuplicates = (): void => {
        getDuplicates({ variables: { input: { tmsIds: [+inputs.tmsId] } } });
    };

    const validateAllBoxes = async (): Promise<boolean> => {
        let hasValidationErrors = false;
        
        // Clear previous box errors
        setBoxError('');
        setSecondaryBoxError('');

        // Validate primary box if selected
        if (inputs.boxId && inputs.boxId !== 0 && !loadDetailData.isBellyDump) {
            try {
                const response = await validateSBXBox({
                    variables: {
                        input: {
                            boxId: inputs.boxId,
                            boxNumber: inputs.boxNumber,
                            isBox: true,
                            isReturnBox: false,
                            returnBoxId1: 0,
                            returnBoxId2: 0,
                            returnBoxName1: null,
                            returnBoxName2: null
                        }
                    }
                });
                                 if (response.data?.validateSBXBox?.errorMessage) {
                     setBoxError(response.data.validateSBXBox.errorMessage);
                     hasValidationErrors = true;
                 }
            } catch (err: any) {
                if (err.graphQLErrors && err.graphQLErrors.length > 0) {
                    const { message } = err.graphQLErrors[0];
                    setBoxError(message.toUpperCase());
                    hasValidationErrors = true;
                } else {
                    console.log("no error");
                }
            }
        }

        // For split loads, we need to check if there's a secondary box selected
        // Since the current implementation uses the same boxId for both, we'll validate based on the split logic
        if (inputs.isSplit && inputs.boxId && inputs.boxId !== 0) {
            try {
                const response = await validateSBXBox({
                    variables: {
                        input: {
                            boxId: inputs.boxId,
                            boxNumber: inputs.boxNumber,
                            isBox: true,
                            isReturnBox: false,
                            returnBoxId1: 0,
                            returnBoxId2: 0,
                            returnBoxName1: null,
                            returnBoxName2: null
                        }
                    }
                });
                
                                 if (response.data?.validateSBXBox?.errorMessage) {
                     setSecondaryBoxError(response.data.validateSBXBox.errorMessage);
                     hasValidationErrors = true;
                 }
            } catch (err: any) {
                if (err.graphQLErrors && err.graphQLErrors.length > 0) {
                    const { message } = err.graphQLErrors[0];
                    setSecondaryBoxError(message.toUpperCase());
                    hasValidationErrors = true;
                } else {
                    console.log("no error");
                }
            }
        }

        return !hasValidationErrors;
    };

    const handleSubmit = async (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ): Promise<void> => {
        if (event) {
            event.preventDefault();
        }

                 // Validate all boxes first
         const boxesValid = await validateAllBoxes();
         
         if (!boxesValid) {
             return; // Stop if box validation failed
         }

        if (loadDetailData.tmsId !== inputs.tmsId && inputs.tmsId !== '') {
            checkForTmsIdDuplicates();
        } else {
            callMutationForEditLoadDetail();
        }
    };

    return {
        handleSubmit,
        handleDropdownChange,
        handleInputChange,
        handleAutoCompleteChange,
        inputs,
        handleCheckBox,
        handleCancel,
        errorMessage,
        handleOnClickOK,
        milesError,
        boxError,
        secondaryBoxError,
        chassisError,
        secondaryChassisError,
        truckNumberError,
        secondaryTruckNumberError,
        tmsIdError,
    };
};

export default useEditLoadDetail;
